// Browser-compatible database implementation using in-memory data
// This is a mock database service for the client-side app

// Import our data models
import meatData from './data/meats.js';
import meatVariantsData from './data/meatVariants.js';
import cheeseData from './data/cheeses.js';
import breadData from './data/breads.js';
import toppingData from './data/toppings.js';
import dressingData from './data/dressings.js';
import styleData from './data/styles.js';
import styleIncludedToppings from './data/styleIncludedToppings.js';
import styleIncludedDressings from './data/styleIncludedDressings.js';
import preparationData from './data/preparations.js';
import temperatureData from './data/temperatures.js';
import specialtySandwichData from './data/specialtySandwiches.js';
import specialtySandwichComponents from './data/specialtySandwichComponents.js';
import specialCombinations from './data/specialCombinations.js';
import dessertData from './data/desserts.js';
import { applyOverridesToData } from '../utils/priceOverrides';

const getDataWithOverrides = (moduleKey, data) => applyOverridesToData(moduleKey, data);

// Database initialization
const initializeDatabase = () => {
  console.log('Browser database mock initialized successfully!');
};

// -------------------- Data Access Methods --------------------

// Meat related queries
const getMeatCategories = () => {
  const meats = getDataWithOverrides('meats', meatData);
  const allCategories = meats.reduce((acc, meat) => {
    if (Array.isArray(meat.category)) {
      acc.push(...meat.category);
    } else if (typeof meat.category === 'string') { // Handle older string format if any persist
      acc.push(meat.category);
    }
    return acc;
  }, []);
  return [...new Set(allCategories)];
};

const getMeatsByCategory = (category) => {
  const meats = getDataWithOverrides('meats', meatData);
  return meats.filter(meat => {
    if (Array.isArray(meat.category)) {
      return meat.category.includes(category);
    } else if (typeof meat.category === 'string') { // Handle older string format if any persist
      return meat.category === category;
    }
    return false;
  });
};

const getAllMeats = () => {
  const meats = [...getDataWithOverrides('meats', meatData)];
  
  // Add variants to each meat
  for (const meat of meats) {
    const variants = getDataWithOverrides('meatVariants', meatVariantsData);
    meat.variants = variants.filter(variant => variant.meat_id === meat.id);
  }
  
  return meats;
};

const getMeatById = (id) => {
  const meats = getDataWithOverrides('meats', meatData);
  const meat = meats.find(meat => meat.id === parseInt(id));
  if (!meat) return null;
  
  // Add variants
  const variants = getDataWithOverrides('meatVariants', meatVariantsData);
  meat.variants = variants.filter(variant => variant.meat_id === meat.id);
  return meat;
};

const getMeatVariants = (meatId) => {
  const variants = getDataWithOverrides('meatVariants', meatVariantsData);
  return variants.filter(variant => variant.meat_id === parseInt(meatId));
};

// Cheese related queries
const getAllCheeses = () => {
  return getDataWithOverrides('cheeses', cheeseData);
};

const getCheeseById = (id) => {
  const cheeses = getDataWithOverrides('cheeses', cheeseData);
  return cheeses.find(cheese => cheese.id === parseInt(id));
};

// Bread related queries
const getAllBreads = () => {
  return getDataWithOverrides('breads', breadData);
};

// Style related queries
const getAllStyles = () => {
  const styles = [...getDataWithOverrides('styles', styleData)];
  
  // Add included items to each style
  for (const style of styles) {
    // Get included toppings
    const includedToppingIds = styleIncludedToppings
      .filter(item => item.style_id === style.id)
      .map(item => item.topping_id);
      
    style.includedToppings = includedToppingIds.map(id => {
      const toppings = getDataWithOverrides('toppings', toppingData);
      const topping = toppings.find(t => t.id === id);
      return { 
        id: topping.id,
        name: topping.name 
      };
    });
    
    // Get included dressings
    const includedDressingIds = styleIncludedDressings
      .filter(item => item.style_id === style.id)
      .map(item => item.dressing_id);
      
    style.includedDressings = includedDressingIds.map(id => {
      const dressings = getDataWithOverrides('dressings', dressingData);
      const dressing = dressings.find(d => d.id === id);
      return { 
        id: dressing.id,
        name: dressing.name 
      };
    });
  }
  
  return styles;
};

const getStyleById = (id) => {
  const styles = getDataWithOverrides('styles', styleData);
  const style = styles.find(style => style.id === parseInt(id));
  
  if (!style) return null;
  
  // Get included toppings
  const includedToppingIds = styleIncludedToppings
    .filter(item => item.style_id === style.id)
    .map(item => item.topping_id);
    
  style.includedToppings = includedToppingIds.map(id => {
    const toppings = getDataWithOverrides('toppings', toppingData);
    const topping = toppings.find(t => t.id === id);
    return { 
      id: topping.id,
      name: topping.name 
    };
  });
  
  // Get included dressings
  const includedDressingIds = styleIncludedDressings
    .filter(item => item.style_id === style.id)
    .map(item => item.dressing_id);
    
  style.includedDressings = includedDressingIds.map(id => {
    const dressings = getDataWithOverrides('dressings', dressingData);
    const dressing = dressings.find(d => d.id === id);
    return { 
      id: dressing.id,
      name: dressing.name 
    };
  });
  
  return style;
};

// Topping and dressing related queries
const getAllToppings = () => {
  return getDataWithOverrides('toppings', toppingData);
};

const getAllDressings = () => {
  return getDataWithOverrides('dressings', dressingData);
};

// Preparation methods
const getAllPreparationMethods = () => {
  return getDataWithOverrides('preparations', preparationData);
};

// Temperature options
const getTemperatureOptions = () => {
  return getDataWithOverrides('temperatures', temperatureData);
};

// Specialty sandwiches
const getAllSpecialtySandwiches = () => {
  return getDataWithOverrides('specialtySandwiches', specialtySandwichData);
};

// Desserts
const getAllDesserts = () => {
  return getDataWithOverrides('desserts', dessertData);
};

const getSpecialtySandwichById = (id) => {
  const sandwiches = getDataWithOverrides('specialtySandwiches', specialtySandwichData);
  const sandwich = sandwiches.find(sandwich => sandwich.id === parseInt(id));
  
  if (!sandwich) return null;
  
  // Get components for this sandwich
  const components = specialtySandwichComponents.filter(
    component => component.specialty_id === sandwich.id
  );
  
  // Process components to get full details
  sandwich.components = components.map(component => {
    let detail = null;
    switch (component.component_type) {
      case 'meat':
        detail = getMeatById(component.component_id);
        break;
      case 'cheese':
        detail = getCheeseById(component.component_id);
        break;
      case 'bread':
        detail = getDataWithOverrides('breads', breadData)
          .find(b => b.id === component.component_id);
        break;
      case 'topping':
        detail = getDataWithOverrides('toppings', toppingData)
          .find(t => t.id === component.component_id);
        break;
      case 'dressing':
        detail = getDataWithOverrides('dressings', dressingData)
          .find(d => d.id === component.component_id);
        break;
      case 'preparation':
        detail = getDataWithOverrides('preparations', preparationData)
          .find(p => p.id === component.component_id);
        break;
      default:
        detail = null;
    }
    
    return {
      type: component.component_type,
      quantity: component.quantity,
      details: detail
    };
  });
  
  return sandwich;
};

// Calculate sandwich price
const calculateSandwichPrice = (options) => {
  const {
    primaryMeat,
    primaryMeatVariant,
    secondaryMeat,
    secondaryMeatVariant,
    selectedCheeses = [],
    style = 1, // Plain by default
    bread,
    preparation = 1, // Regular by default
    toppings = [],
    dressings = [],
    extraMeatQuantity = 1 // Default to 1 oz if not specified
  } = options;
  
  let totalPrice = 0;
  
  // Get primary meat price
  if (primaryMeat) {
    const meat = getMeatById(primaryMeat);
    if (meat) {
      totalPrice += meat.base_price;
      
      // Add variant adjustment if any
      if (primaryMeatVariant) {
        const variants = getDataWithOverrides('meatVariants', meatVariantsData);
        const variant = variants.find(
          v => v.meat_id === parseInt(primaryMeat) && v.id === parseInt(primaryMeatVariant)
        );
        if (variant) {
          totalPrice += variant.price_adjustment;
        }
      }
    }
  }
  
  // Check for special combination price
  if (primaryMeat && secondaryMeat) {
    const combos = getDataWithOverrides('specialCombinations', specialCombinations);
    const combo = combos.find(
      c => (c.primary_meat_id === parseInt(primaryMeat) && c.secondary_meat_id === parseInt(secondaryMeat)) ||
           (c.primary_meat_id === parseInt(secondaryMeat) && c.secondary_meat_id === parseInt(primaryMeat))
    );
    
    if (combo) {
      // Use special combo price instead of individual meat prices
      totalPrice = combo.combo_price;
    } else {
      // For non-special combos, find the higher priced meat
      const secondaryMeatObj = getMeatById(secondaryMeat);
      if (secondaryMeatObj) {
        // Get the base prices with variants for comparison
        let primaryPrice = totalPrice; // Already includes primary meat + variant
        let secondaryPrice = secondaryMeatObj.base_price;
        
        // Add variant adjustment to secondary price if any
        if (secondaryMeatVariant) {
          const variants = getDataWithOverrides('meatVariants', meatVariantsData);
          const variant = variants.find(
            v => v.meat_id === parseInt(secondaryMeat) && v.id === parseInt(secondaryMeatVariant)
          );
          if (variant) {
            secondaryPrice += variant.price_adjustment;
          }
        }
        
        // Use ONLY the higher price as the base, DO NOT add prices together for combos
        if (secondaryPrice > primaryPrice) {
          totalPrice = secondaryPrice;
        }
        // Important: We DO NOT add meat prices together for regular combos
      }
    }
  }
  
  // Add style price adjustment
  if (style) {
    const styles = getDataWithOverrides('styles', styleData);
    const styleObj = styles.find(s => s.id === parseInt(style));
    if (styleObj) {
      totalPrice += styleObj.price_adjustment;
      
      // Handle double meat for New Yorker style
      // NOTE: This is different from combo sandwiches!
      // New Yorker style explicitly adds a second portion of the same meat
      // The price adjustment already includes the extra meat, so we don't need to add it again
      // DO NOT add the meat price again for New Yorker style
    }
  }
  
  // Add bread price - ensure it's applied for all styles including New Yorker
  if (bread) {
    const breads = getDataWithOverrides('breads', breadData);
    const breadObj = breads.find(b => b.id === parseInt(bread));
    if (breadObj) {
      totalPrice += breadObj.price_adjustment;
    }
  }
  
  // Add preparation method price
  if (preparation) {
    const preps = getDataWithOverrides('preparations', preparationData);
    const prepObj = preps.find(p => p.id === parseInt(preparation));
    if (prepObj) {
      totalPrice += prepObj.price_adjustment;
    }
  }
  
  // Handle cheese prices
  const isSpecialStyle = style === 2 || style === 3; // Special or Super
  const isNewYorkerStyle = style === 4; // New Yorker
  const isPlainStyle = style === 1 || !style; // Plain style or no style selected

  // Check for "No Cheese" option
  if (selectedCheeses.length === 1 && selectedCheeses[0].id === 0) {
    // If "No Cheese" is selected with a style that includes cheese, apply discount
    if (isSpecialStyle) {
      totalPrice -= 1.50; // Apply discount for removing included cheese
    }
    // For plain style, no cheese is already the default, so no price change
  } else {
    // Normal cheese pricing
    for (const cheese of selectedCheeses) {
      // For Special and Super styles, Swiss cheese (id 1) is included
      // For all other cheeses or styles, add the price
      let isIncluded = false;
      
      // Check if this is Swiss cheese (id 1) and we're using a style that includes it
      if (isSpecialStyle && parseInt(cheese.id) === 1 && cheese.quantity === 'single') {
        isIncluded = true;
      }
      
      // New Yorker and Plain styles ALWAYS charge for cheese, no included cheese
      if (isNewYorkerStyle || isPlainStyle || !isIncluded) {
        const cheeses = getDataWithOverrides('cheeses', cheeseData);
        const cheeseObj = cheeses.find(c => c.id === parseInt(cheese.id));
        if (cheeseObj) {
          let multiplier = 1;
          if (cheese.quantity === 'double') multiplier = 2;
          if (cheese.quantity === 'triple') multiplier = 3;
          
          totalPrice += cheeseObj.base_price * multiplier;
        }
      }
    }
  }
  
  // Get style included toppings
  let includedToppings = [];
  let includedDressings = [];
  
  if (style && style !== 1) { // Not Plain style
    // Get included toppings
    includedToppings = styleIncludedToppings
      .filter(item => item.style_id === parseInt(style))
      .map(item => item.topping_id);
    
    // Get included dressings
    includedDressings = styleIncludedDressings
      .filter(item => item.style_id === parseInt(style))
      .map(item => item.dressing_id);
  }
  
  // Calculate missing included toppings and apply discount if removed
  const missingIncludedToppings = includedToppings.filter(id => !toppings.includes(id));
  for (const missingId of missingIncludedToppings) {
    const toppingsData = getDataWithOverrides('toppings', toppingData);
    const topping = toppingsData.find(t => t.id === missingId);
    if (topping && topping.price > 0) {
      // Apply discount for removing included premium toppings
      totalPrice -= topping.price;
    }
  }

  // Add toppings prices (excluding those included with the style)
  for (const toppingId of toppings) {
    // For New Yorker style, always add the price of toppings
    // For other styles, only add price if not included
    const isNewYorkerStyle = style === 4;
    
    if (isNewYorkerStyle || !includedToppings.includes(parseInt(toppingId))) {
      const toppingsData = getDataWithOverrides('toppings', toppingData);
      const topping = toppingsData.find(t => t.id === parseInt(toppingId));
      if (topping) {
        // Special handling for Extra Meat (ID 14) - use quantity from options
        if (topping.id === 14) {
          // Add price multiplied by quantity from options
          totalPrice += topping.price * extraMeatQuantity;
        } else {
          // Regular topping price
          totalPrice += topping.price;
        }
      }
    }
  }
  
  // Handle dressing prices - first two are free, additional ones are $1.25 each
  const extraDressingsCount = Math.max(0, dressings.length - 2);
  if (extraDressingsCount > 0) {
    totalPrice += extraDressingsCount * 1.25;
  }
  
  // Calculate missing included dressings but no price impact for these
  
  return {
    totalPrice: parseFloat(totalPrice.toFixed(2)),
    includedToppings,
    includedDressings
  };
};

// Constants
const BASE_PRICE = 14.99;

export {
  initializeDatabase,
  getMeatCategories,
  getMeatsByCategory,
  getAllMeats,
  getMeatById,
  getMeatVariants,
  getAllCheeses,
  getCheeseById,
  getAllBreads,
  getAllStyles,
  getStyleById,
  getAllToppings,
  getAllDressings,
  getAllPreparationMethods,
  getTemperatureOptions,
  getAllSpecialtySandwiches,
  getSpecialtySandwichById,
  getAllDesserts,
  calculateSandwichPrice,
  BASE_PRICE
};